import { FormEventHandler, useEffect, useState } from "react";
import ReactQuill, { Quill } from "react-quill";

import ImageResize from "quill-image-resize-module-react";
import classNames from "classnames";
import {
    Form,
    Link,
    Params,
    useLoaderData,
    useNavigate,
} from "react-router-dom";
import Swal from "sweetalert2";
import { API_URL } from "../../../lib/config";
import useLogin from "../../../hooks/useLogin";
import { Post } from "../../../lib/post";

import chevronLeft from "../../../assets/images/icons/chevron-back-outline.svg";
import trashIcon from "../../../assets/images/icons/trash-outline.svg";
import eyeIcon from "../../../assets/images/icons/eye-outline.svg";

import "react-quill/dist/quill.snow.css";

Quill.register("modules/imageResize", ImageResize);
window["Quill" as any] = Quill as any;

const modules = {
    toolbar: [
        [{ header: [1, 2, false] }],
        ["bold", "italic", "underline", "strike", "blockquote"],
        [
            { list: "ordered" },
            { list: "bullet" },
            { indent: "-1" },
            { indent: "+1" },
        ],
        ["link", "image"],
        ["clean"],
    ],
    imageResize: {
        parchment: Quill.import("parchment"),
        modules: ["Resize", "DisplaySize"],
    },
};

const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
];

export async function loader({ params }: { params: Params }) {
    const id = params.id;

    let post: Post | null = null;

    try {
        const response = await fetch(API_URL + "/posts/" + id);
        if (response.ok) {
            post = await response.json();
        }
    } catch (error) {
        console.error(error);
    }

    if (!post) {
        throw new Error("Post not found");
    }

    return post;
}

export default function EditPost() {
    const post = useLoaderData() as Awaited<ReturnType<typeof loader>>;

    const [userStatus, auth] = useLogin("/login");

    const [content, setContent] = useState(post.content || "");
    const [title, setTitle] = useState(post.title || "");
    const navigate = useNavigate();

    function deletePost() {
        Swal.fire({
            title: "Apagar Post?",
            html: "Tem certeza que você quer apagar este post? Essa ação é <strong>IRREVERSÍVEL</strong>!",

            color: "#ffffff",
            background: "#121212",

            confirmButtonText: "Sim, apagar post!",
            confirmButtonColor: "#f00000",

            showCancelButton: true,

            focusCancel: true,
            cancelButtonText: "Cancelar",
        }).then((result) => {
            if (result.value) {
                const headers = new Headers();
                headers.append("Authorization", "Basic " + auth);

                fetch(API_URL + "/posts/delete/" + post.id, {
                    method: "DELETE",
                    headers,
                })
                    .then((res) => {
                        if (res.ok) {
                            navigate("/dashboard");
                        } else {
                            alert("O post NÂO pôde ser apagado!");
                        }
                    })
                    .catch((error) => {
                        alert("O post NÂO pôde ser apagado!");
                        console.error(error);
                    });
            }
        });
    }

    const save: FormEventHandler = (e) => {
        e.preventDefault();

        const headers = new Headers();
        headers.append("Authorization", "Basic " + auth);
        headers.append("Content-Type", "application/json");

        const update: { [x: string]: string } = {};
        if (title) {
            update["title"] = title;
        }

        if (content) {
            update["content"] = content;
        }

        fetch(API_URL + "/posts/update/" + post.id, {
            method: "PUT",
            headers,
            body: JSON.stringify(update),
        })
            .then((res) => {
                if (res.ok) {
                    Swal.fire({
                        title: "Post salvo!",
                        html: "O post foi salvo com sucesso!",

                        color: "#ffffff",
                        background: "#121212",

                        confirmButtonText: "Ok!",
                        confirmButtonColor: "#f00000",
                    });
                } else {
                    alert("O post NÃO pôde ser salvo!");
                }
            })
            .catch((error) => {
                alert("O post NÂO pôde ser salvo!");
                console.error(error);
            });
    };

    if (userStatus === "pending") {
        return <h1>Aguardando Autorização...</h1>;
    }

    return (
        <div className="min-h-vh mb-16 mt-8 flex w-11/12 flex-col gap-8 md:w-3/4">
            <div className="w-full flex flex-col gap-2">
                <div className="flex w-full flex-wrap justify-between">
                    <Link
                        className="flex w-fit items-center gap-2 rounded-md border border-xv-border-color bg-xv-bg-2 px-4 py-2 hocus:brightness-90"
                        to={"/blog"}
                    >
                        <img className="w-6" src={chevronLeft} alt="<" />
                        <span className="leading-none">Voltar</span>
                    </Link>
                    <button
                        onClick={deletePost}
                        className="flex w-fit gap-2 self-end rounded-md border border-xv-border-color bg-xv-bg-2 px-4 py-2 font-medium hocus:brightness-90"
                    >
                        <img
                            className="w-5"
                            src={trashIcon}
                            alt="Ícono de Lixeira"
                        />
                        <span>Apagar Post</span>
                    </button>
                </div>

                <Link
                    className="self-end flex w-fit gap-1 whitespace-nowrap rounded-md border border-xv-border-color bg-xv-bg-2 px-4 py-2 font-medium hocus:brightness-90"
                    target="_blank"
                    to={"/blog/" + post.id}
                >
                    <img className="w-5" src={eyeIcon} alt="Ícone de olho" />
                    <span>Ver Post</span>
                </Link>
            </div>

            <form
                onSubmit={save}
                className="flex h-full w-full flex-col items-center gap-8"
            >
                <input
                    className={classNames(
                        "w-full border-b border-xv-border-color bg-transparent px-2 py-1 outline-none transition-all focus:border-white focus:outline-none"
                    )}
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Título"
                />
                <input type="hidden" name="content" value={content} />

                <ReactQuill
                    className="w-full"
                    theme="snow"
                    value={content}
                    onChange={setContent}
                    modules={modules}
                    formats={formats}
                />

                <div className="mt-12 flex flex-col items-center gap-4">
                    <button
                        className=" w-fit rounded-md bg-xv-red px-12 py-2 font-medium hocus:brightness-90"
                        type="submit"
                    >
                        Publicar
                    </button>
                </div>
            </form>
        </div>
    );
}
